import { useEffect, useState } from 'react';
import { Snackbar, Alert, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Listens for the beforeinstallprompt event and surfaces a small "Install"
 * snackbar in the corner. Persistent dismissal is stored in localStorage so
 * we don't nag the user every load.
 *
 * iOS Safari doesn't fire beforeinstallprompt — for that we'd need a
 * separate "Add to Home Screen" instruction overlay. Out of scope for this
 * pass; this covers Chrome / Edge / Brave / Android.
 */
type DeferredPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const DISMISS_KEY = 'pwa.install.dismissedAt';
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000; // re-prompt after 14 days

function recentlyDismissed(): boolean {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    if (!ts || Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState<DeferredPrompt | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (recentlyDismissed()) return;
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as DeferredPrompt);
      setOpen(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    setOpen(false);
    try {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === 'dismissed') {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
      }
    } finally {
      setDeferred(null);
    }
  };

  const handleDismiss = () => {
    setOpen(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
  };

  if (!deferred) return null;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{ mb: { xs: 9, md: 2 } }} // sit above mobile bottom nav
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{ alignItems: 'center', '& .MuiAlert-message': { display: 'flex', alignItems: 'center', gap: 1.5 } }}
        action={
          <>
            <Button color="inherit" size="small" onClick={handleInstall}>Install</Button>
            <IconButton color="inherit" size="small" onClick={handleDismiss} aria-label="Dismiss install prompt">
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      >
        Install MoreMe AI for offline access and a native-app feel.
      </Alert>
    </Snackbar>
  );
}
